import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, type OrderDetail, type OrderSummary, type Operation, type Station } from '../lib/api.ts';
import { useAuth } from '../App.tsx';
import {
  BREAKS, OVERTIME_BREAK_MIN, SHIFT_END_MIN, SHIFT_START_MIN,
  formatDuration, formatDecimalHours, formatMinutes, netMinutes, parseTimeToMinutes, todayInPlant,
} from '../../shared/time.ts';
import { policz } from '../../shared/pl.ts';

/**
 * Rejestracja pracy — ekran, na którym pracownik spędza kilkanaście sekund dziennie.
 *
 * Różnice wobec Excela, celowe:
 *  - nie ma pola "ilu pracowników" (aplikacja wylicza sama z nakładających się wpisów),
 *  - nie ma pola "ilość agregatów" (wynika z zaznaczonych numerów seryjnych),
 *  - jeden wpis obejmuje wiele numerów seryjnych, więc 6 sztuk to jeden formularz.
 */
export default function Register() {
  const { user } = useAuth();

  const [stations, setStations] = useState<Station[]>([]);
  const [operations, setOperations] = useState<Operation[]>([]);
  const [station, setStation] = useState<string | null>(null);
  // Wiele operacji naraz: pracownik przy jednym zleceniu robi w tym samym bloku
  // czasu np. prefabrykację wiązki, prefabrykację elektryczną i montaż.
  const [operacje, setOperacje] = useState<Set<string>>(new Set());

  const [zpQuery, setZpQuery] = useState('');
  const [matches, setMatches] = useState<OrderSummary[]>([]);
  // Numer wpisany do pola po wyborze z listy — inaczej wyszukiwarka od razu
  // pokazywałaby podpowiedzi dla właśnie wybranego zlecenia.
  const [chosenZp, setChosenZp] = useState<string | null>(null);
  const [detail, setDetail] = useState<OrderDetail | null>(null);
  const [picked, setPicked] = useState<Set<string>>(new Set());

  const [date, setDate] = useState(todayInPlant());
  const [start, setStart] = useState('06:00');
  const [stop, setStop] = useState('14:00');
  const [breaks, setBreaks] = useState<Record<string, boolean>>(
    Object.fromEntries(BREAKS.map((b) => [b.key, true])),
  );
  const [overtimeBreak, setOvertimeBreak] = useState(false);
  const [isPrototype, setIsPrototype] = useState(false);
  const [isFinished, setIsFinished] = useState(false);
  const [notes, setNotes] = useState('');

  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState<{ kind: 'ok' | 'err'; text: string } | null>(null);

  // --- słowniki -------------------------------------------------------------
  useEffect(() => {
    api.get<{ stations: Station[]; operations: Operation[] }>('/catalog').then((r) => {
      setStations(r.stations);
      setOperations(r.operations);
      setStation((s) => s ?? user?.station_code ?? r.stations.find((x) => !x.is_parked)?.code ?? null);
    });
  }, [user?.station_code]);

  // Operacje dostępne z wybranego kafla. Przypisanie idzie z tabeli wiele-do-wielu,
  // więc freon pojawia się wszędzie bez żadnego wyjątku w kodzie. Globalne mają
  // sort_order 900, przez co lądują na końcu listy — freon jest dokładką do każdego
  // stanowiska, a nie domyślną robotą elektryka.
  const stationOps = useMemo(
    () => operations.filter((o) => (o.stations ?? [o.station_code]).includes(station ?? '')),
    [operations, station],
  );

  // Kafle stanowisk grupujemy po rodzicu: Sniffer i Wytrzymałość stoją pod
  // nagłówkiem Kontroli jakości, bo tam należą, ale wybiera się je osobno.
  const grupyStanowisk = useMemo(() => {
    const rodzice = stations.filter((s) => !s.parent_code);
    return rodzice.map((r) => ({
      rodzic: r,
      dzieci: stations.filter((s) => s.parent_code === r.code),
    }));
  }, [stations]);

  // Zmiana stanowiska czyści wybór — operacje z poprzedniej strefy nie mają tu sensu.
  // Nic nie zaznaczamy domyślnie: pracownik ma świadomie wskazać, co robił.
  useEffect(() => { setOperacje(new Set()); }, [station]);

  const przelaczOperacje = (kod: string) =>
    setOperacje((prev) => {
      const next = new Set(prev);
      next.has(kod) ? next.delete(kod) : next.add(kod);
      return next;
    });

  // --- wyszukiwanie zlecenia ------------------------------------------------
  useEffect(() => {
    const q = zpQuery.trim();
    if (q.length < 2 || q === chosenZp) { setMatches([]); return; }
    const t = setTimeout(() => {
      api.get<{ orders: OrderSummary[] }>(`/orders/search?q=${encodeURIComponent(q)}`)
        .then((r) => setMatches(r.orders))
        .catch(() => setMatches([]));
    }, 220);
    return () => clearTimeout(t);
  }, [zpQuery, chosenZp]);

  const openOrder = async (zp: string) => {
    const pierwsza = [...operacje][0];
    const d = await api.get<OrderDetail>(
      `/orders/${encodeURIComponent(zp)}${pierwsza ? `?operation=${pierwsza}` : ''}`,
    );
    setDetail(d);
    setMatches([]);
    setChosenZp(d.order.zp_number);
    setZpQuery(d.order.zp_number);
    setPicked(new Set());
  };

  // Kto wziął którą sztukę zależy od operacji, więc po zmianie zaznaczenia
  // odświeżamy stan zlecenia.
  useEffect(() => {
    if (detail) void openOrder(detail.order.zp_number);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operacje]);

  // --- czas -----------------------------------------------------------------
  const startMin = parseTimeToMinutes(start);
  const endMin = parseTimeToMinutes(stop);
  const worked = startMin !== null && endMin !== null
    ? netMinutes({ start_min: startMin, end_min: endMin, ...breaks, br_overtime: overtimeBreak })
    : 0;
  const isOvertime = endMin !== null && endMin > SHIFT_END_MIN;

  useEffect(() => { if (!isOvertime) setOvertimeBreak(false); }, [isOvertime]);

  // Norma wpisu = suma norm wszystkich zaznaczonych operacji na wszystkich sztukach.
  const normyOperacji = detail?.normy ?? {};
  const normaNaSztuke = [...operacje].reduce<number | null>((sum, kod) => {
    const h = normyOperacji[kod];
    return h === undefined || sum === null ? null : sum + h;
  }, 0);
  const normForPicked = normaNaSztuke !== null ? normaNaSztuke * 60 * picked.size : null;
  const brakNormy = normaNaSztuke === null && operacje.size > 0;

  const togglePick = (serial: string) =>
    setPicked((prev) => {
      const next = new Set(prev);
      next.has(serial) ? next.delete(serial) : next.add(serial);
      return next;
    });

  const canSave =
    !!station && operacje.size > 0 && startMin !== null && endMin !== null &&
    startMin !== endMin && (isPrototype || picked.size > 0);

  const save = async () => {
    if (!canSave) return;
    setSaving(true);
    setFlash(null);
    try {
      await api.post('/entries', {
        work_date: date,
        station_code: station,
        operations: [...operacje],
        zp_number: detail?.order.zp_number ?? null,
        serials: [...picked],
        start_min: startMin,
        end_min: endMin,
        ...breaks,
        br_overtime: overtimeBreak,
        is_prototype: isPrototype,
        is_finished: isFinished,
        notes: notes.trim() || null,
      });
      setFlash({
        kind: 'ok',
        text: `Zapisano ${formatDuration(worked)} — ${picked.size ? policz(picked.size, 'sztuka') : 'prototyp'}`,
      });
      setPicked(new Set());
      setNotes('');
      setIsFinished(false);
      if (detail) void openOrder(detail.order.zp_number);
    } catch (e: any) {
      setFlash({ kind: 'err', text: e.message ?? 'Nie udało się zapisać' });
    } finally {
      setSaving(false);
    }
  };

  const parkedStation = stations.find((s) => s.code === station)?.is_parked;

  return (
    <div className="space-y-4 pb-24">
      <h1 className="text-head-sm">Rejestracja pracy</h1>

      {/* 1. stanowisko */}
      <Krok numer={1} tytul="Stanowisko">
        <div className="space-y-3">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {grupyStanowisk.filter((g) => g.dzieci.length === 0).map(({ rodzic: s }) => (
              <KafelStanowiska key={s.code} s={s} wybrane={station} onWybor={setStation} />
            ))}
          </div>
          {grupyStanowisk.filter((g) => g.dzieci.length > 0).map(({ rodzic, dzieci }) => (
            <div key={rodzic.code} className="rounded-lg border border-line p-3">
              <div className="text-label uppercase text-plum-muted mb-2">{rodzic.name}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                {[rodzic, ...dzieci]
                  .filter((s) => s.code !== rodzic.code || stationOpsMa(operations, rodzic.code))
                  .map((s) => (
                    <KafelStanowiska key={s.code} s={s} wybrane={station} onWybor={setStation} />
                  ))}
              </div>
            </div>
          ))}
        </div>
        {!!parkedStation && (
          <p className="mt-3 text-body-sm text-warn bg-warn/[0.06] border border-warn/25 rounded-lg px-3 py-2">
            Rurki są w trakcie wdrożenia giętarki — brak kompletu czasów normatywnych.
            Możesz rejestrować pracę, ale efektywność policzymy dopiero po uzupełnieniu norm.
          </p>
        )}
      </Krok>

      {/* 2. operacja */}
      <Krok numer={2} tytul="Co robisz">
        <div className="grid sm:grid-cols-2 gap-2">
          {stationOps.map((o) => {
            const wybrana = operacje.has(o.code);
            return (
              <button
                key={o.code}
                className={`tile ${wybrana ? 'tile-on' : ''}`}
                aria-pressed={wybrana}
                onClick={() => przelaczOperacje(o.code)}
              >
                <span className="font-semibold">{o.name}</span>
                <span className="flex items-center gap-2 shrink-0">
                  {!!o.is_global && (
                    <span className={`text-caption px-1.5 py-0.5 rounded-pill border ${
                      wybrana ? 'border-white/40 text-white/80' : 'border-line text-plum-muted'}`}>
                      każde stanowisko
                    </span>
                  )}
                  <Ptaszek on={wybrana} />
                </span>
              </button>
            );
          })}
        </div>
        <p className="mt-3 text-caption text-plum-muted">
          Możesz zaznaczyć kilka operacji naraz, jeśli robiłeś je w tym samym czasie.
          Aplikacja rozdzieli czas między nie i zsumuje normy.
        </p>
      </Krok>

      {/* 3. zlecenie i sztuki */}
      <Krok numer={3} tytul="Zlecenie i numery seryjne">
        <label className="label" htmlFor="zp">Numer ZP lub numer seryjny</label>
        <input
          id="zp"
          className="field field-lg"
          placeholder="np. ZP-828/2025/PR albo 2500"
          value={zpQuery}
          onChange={(e) => { setZpQuery(e.target.value); setDetail(null); setChosenZp(null); }}
          autoComplete="off"
        />

        {matches.length > 0 && (
          <ul className="mt-2 border border-line rounded-lg divide-y divide-line overflow-hidden">
            {matches.map((m) => (
              <li key={m.zp_number}>
                <button className="w-full text-left px-3 py-3 hover:bg-canvas transition-colors"
                        onClick={() => openOrder(m.zp_number)}>
                  <span className="font-semibold text-plum">{m.zp_number}</span>
                  <span className="text-plum-soft text-body-sm ml-2">{m.product_name}</span>
                  <span className="text-plum-muted text-caption ml-2 tnum">{policz(m.qty, 'sztuka')}</span>
                </button>
              </li>
            ))}
          </ul>
        )}

        {detail && (
          <div className="mt-4">
            <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1 mb-3">
              <span className="font-semibold text-plum">{detail.order.zp_number}</span>
              <span className="text-body-sm text-plum-soft">{detail.order.product_name}</span>
              {detail.order.customer && <span className="text-caption text-plum-muted">{detail.order.customer}</span>}
              <span className="ml-auto text-body-sm tnum text-plum-soft">
                rozpoczęte <b className="text-plum">{detail.startedCount}</b> z {detail.totalCount}
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {detail.serials.map((s) => {
                const on = picked.has(s.serial_no);
                return (
                  <button key={s.serial_no} className={`tile !min-h-[64px] ${on ? 'tile-on' : ''}`}
                          aria-pressed={on} onClick={() => togglePick(s.serial_no)}>
                    <span className="min-w-0">
                      <span className="block font-semibold tnum">{s.serial_no}</span>
                      {s.workedBy.length > 0 && (
                        <span className={`block text-caption truncate ${on ? 'text-white/70' : 'text-plum-muted'}`}>
                          {s.isFinished ? 'zakończone · ' : 'w toku · '}{s.workedBy.join(', ')}
                        </span>
                      )}
                    </span>
                    <Ptaszek on={on} />
                  </button>
                );
              })}
            </div>

            {picked.size > 0 && (
              <p className="mt-3 text-body-sm text-plum-soft">
                Wybrano <b className="text-plum tnum">{picked.size}</b> z {policz(detail.totalCount, 'sztuka')}
                {normForPicked !== null && (
                  <> · norma <b className="text-plum tnum">{formatDuration(normForPicked)}</b>
                    <span className="text-plum-muted"> ({formatDecimalHours(normForPicked)})</span></>
                )}
                {brakNormy && !isPrototype && (
                  <span className="text-warn"> · brak normy dla części zaznaczonych operacji</span>
                )}
              </p>
            )}
          </div>
        )}
      </Krok>

      {/* 4. czas */}
      <Krok numer={4} tytul="Godziny">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="start">Start</label>
            <input id="start" type="time" step={300} className="field field-lg tnum" value={start}
                   onChange={(e) => setStart(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="stop">Stop</label>
            <input id="stop" type="time" step={300} className="field field-lg tnum" value={stop}
                   onChange={(e) => setStop(e.target.value)} />
          </div>
        </div>

        <div className="mt-4">
          <span className="label">Przerwy w tym czasie</span>
          <div className="grid sm:grid-cols-2 gap-2">
            {BREAKS.map((b) => (
              <Zaznacz
                key={b.key}
                checked={!!breaks[b.key]}
                onChange={(v) => setBreaks((p) => ({ ...p, [b.key]: v }))}
                label={b.label}
                hint={b.hint ?? `${b.minutes} min`}
              />
            ))}
            {isOvertime && (
              <Zaznacz
                checked={overtimeBreak}
                onChange={setOvertimeBreak}
                label="Przerwa za nadgodziny"
                hint={`${OVERTIME_BREAK_MIN} min`}
              />
            )}
          </div>
        </div>

        <div className="mt-4 flex items-baseline gap-2 border-t border-line pt-3">
          <span className="text-body-sm text-plum-soft">Czas pracy netto</span>
          <span className="ml-auto kpi-sm">{formatDuration(worked)}</span>
          <span className="text-body-sm text-plum-muted tnum">{formatDecimalHours(worked)}</span>
        </div>
        {isOvertime && (
          <p className="mt-1 text-caption text-plum-muted">
            Zakończenie po {formatMinutes(SHIFT_END_MIN)} — nadgodziny liczone od końca zmiany.
          </p>
        )}
        {startMin !== null && startMin < SHIFT_START_MIN && (
          <p className="mt-1 text-caption text-warn">
            Start przed {formatMinutes(SHIFT_START_MIN)} — sprawdź, czy to na pewno tak.
          </p>
        )}
      </Krok>

      {/* 5. dodatkowe */}
      <Krok numer={5} tytul="Dodatkowe">
        <div className="grid sm:grid-cols-2 gap-2">
          <Zaznacz checked={isPrototype} onChange={setIsPrototype} label="Prototyp"
                   hint="brak normy — liczy się czas rzeczywisty" />
          <Zaznacz checked={isFinished} onChange={setIsFinished} label="Zlecenie zakończone"
                   hint="operacja domknięta na tych sztukach" />
        </div>
        <div className="mt-3 grid sm:grid-cols-2 gap-3">
          <div>
            <label className="label" htmlFor="notes">Uwagi</label>
            <input id="notes" className="field" placeholder="opcjonalnie" value={notes}
                   onChange={(e) => setNotes(e.target.value)} />
          </div>
          <div>
            <label className="label" htmlFor="date">Data</label>
            <input id="date" type="date" className="field tnum" value={date} max={todayInPlant()}
                   onChange={(e) => setDate(e.target.value)} />
            {date !== todayInPlant() && (
              <p className="mt-1 text-caption text-warn">Wpis wstecz — zostanie oznaczony w raportach.</p>
            )}
          </div>
        </div>
      </Krok>

      {/* pasek zapisu */}
      <div className="fixed bottom-0 inset-x-0 z-30 bg-white/95 backdrop-blur border-t border-line">
        <div className="mx-auto max-w-content px-5 py-3 flex items-center gap-3">
          {flash && (
            <span className={`text-body-sm truncate ${flash.kind === 'ok' ? 'text-ok' : 'text-bad'}`} role="status">
              {flash.text}
            </span>
          )}
          <button className="btn-primary btn-lg ml-auto" disabled={!canSave || saving} onClick={save}>
            {saving ? 'Zapisywanie…' : 'Zapisz wpis'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- drobne

/** Czy pod kaflem jest cokolwiek do wybrania — pusty kafel nie ma po co istnieć. */
function stationOpsMa(operations: Operation[], kod: string): boolean {
  return operations.some((o) => (o.stations ?? [o.station_code]).includes(kod));
}

function KafelStanowiska({ s, wybrane, onWybor }: {
  s: Station; wybrane: string | null; onWybor: (kod: string) => void;
}) {
  const on = wybrane === s.code;
  return (
    <button className={`tile ${on ? 'tile-on' : ''}`} aria-pressed={on} onClick={() => onWybor(s.code)}>
      <span className="font-semibold">{s.name}</span>
      {!!s.is_parked && (
        <span className={`text-caption px-1.5 py-0.5 rounded-pill border ${
          on ? 'border-white/40 text-white/80' : 'border-line text-plum-muted'}`}>
          wkrótce
        </span>
      )}
    </button>
  );
}

function Krok({ numer, tytul, children }: { numer: number; tytul: string; children: ReactNode }) {
  return (
    <section className="card p-4 sm:p-5">
      <h2 className="flex items-center gap-2.5 mb-3.5 text-sub">
        <span className="w-6 h-6 rounded-pill bg-canvas border border-line grid place-items-center
                         text-caption font-semibold tnum text-plum-muted shrink-0">
          {numer}
        </span>
        {tytul}
      </h2>
      {children}
    </section>
  );
}

/** Kwadracik z ptaszkiem — jeden wygląd dla operacji, sztuk i przerw. */
function Ptaszek({ on }: { on: boolean }) {
  return (
    <span aria-hidden className={`shrink-0 w-5 h-5 rounded-sm border grid place-items-center text-caption ${
      on ? 'bg-white text-plum border-white' : 'border-line-strong'}`}>
      {on ? '✓' : ''}
    </span>
  );
}

function Zaznacz({
  checked, onChange, label, hint,
}: { checked: boolean; onChange: (v: boolean) => void; label: string; hint?: string }) {
  return (
    <label className={`tap tile !min-h-[52px] cursor-pointer ${checked ? 'tile-on' : ''}`}>
      <span className="min-w-0">
        <span className="block text-body-sm font-semibold truncate">{label}</span>
        {hint && <span className={`block text-caption ${checked ? 'text-white/70' : 'text-plum-muted'}`}>{hint}</span>}
      </span>
      <input type="checkbox" className="sr-only" checked={checked} onChange={(e) => onChange(e.target.checked)} />
      <Ptaszek on={checked} />
    </label>
  );
}
